// The office app's header, as the Riverstone prototypes draw it (docs/design/fidelity-pass.md):
// the company mark, at most five pill tabs, the date with a big time and sign-out on the right;
// then the screen's title (38 px) with its own tools. Places that share a tab (Book a job and
// Customers under Book) show a sub-nav under the title. A ringing call counts on Calls wherever
// you are.
//
// Adding a place (any track): one row in PLACES (with the permissions that open it, the one its
// screen needs, and the tab it sits under) and one entry in App.tsx's screen
// switch. A section with its own sub-pages (Books, Settings) keeps its inner nav inside its screen.
//
// The bell beside Help is the notifications place (QA-097, shared/Notifications.tsx); App owns its
// list and where each notification opens.

import { useContext, useEffect, useId, useState, type ReactNode } from 'react';
import { dateText, timeParts } from '@flowology/kernel';
import { initials } from './shared/initials.ts';
import { holdsAny } from './shared/may.ts';
import { AccountBanner } from './account/Banner.tsx';
import { TwoFactorBanner } from './account/TwoFactorBanner.tsx';
import { MigrationBanner } from './migration/MigrationBanner.tsx';
import { TrialBanner } from './migration/TrialBanner.tsx';
import { forgetSessionCaches } from './shared/session-caches.ts';
import { HelpDialog } from './shared/Help.tsx';
import { NotificationsBell, type NotesState } from './shared/Notifications.tsx';
import { CrumbContext, useDesign } from './blueprint/design.ts';
import './blueprint/crumb.css';
import type { Note } from './shared/notifications-model.ts';

export type View =
  | 'calls'
  | 'book'
  | 'board'
  | 'hours'
  | 'customers'
  | 'tobook'
  | 'review'
  | 'messages'
  | 'lists'
  | 'tasks'
  | 'books'
  | 'settings'
  | 'agents'
  | 'company'
  | 'memberships'
  | 'inventory'
  | 'migration'
  | 'huddle'
  | 'reports'
  /** Your sign-in (QA-093): everyone's own, from the header, not a place in the tabs. */
  | 'signin'
  /** Home's Needs you: Blueprint's alone (blueprint/Home.tsx), so never in PLACES or Riverstone's tabs. */
  | 'home'
  /** Marketing's Ads and calls, Blueprint's alone too (blueprint/Ads.tsx): how the ads did (#/marketing,
   *  once #/ads), the campaigns, and today's calls by source. */
  | 'marketing'
  | 'adcampaigns'
  | 'adleads'
  /** Operations' Profit, Blueprint's alone (blueprint/Profit.tsx): a day's gross profit (#/operations, once
   *  #/profit), and by job and technician. */
  | 'operations'
  | 'profitjobs'
  /** Home's Performance, Blueprint's alone (blueprint/Performance.tsx, BL-123). */
  | 'performance'
  /** Schedule's Map, Who's working and Time off, Blueprint's alone (schedule/SchedulePages.tsx). */
  | 'map'
  | 'team'
  | 'timeoff'
  /** Inbox's To approve (one request on its own page) and Notifications, Blueprint's alone (inbox/InboxPages.tsx). */
  | 'approval'
  | 'notifications'
  /** Customers' Equipment, every customer's in one list (history/EquipmentPage.tsx), Blueprint's alone. */
  | 'equipment'
  /** Stock's Catalog and prices: the price book's parts as stock keeps them (inventory/Catalog.tsx),
   *  Blueprint's alone. */
  | 'catalog'
  /** Setup's Your plan and data (Company's subscription and data) and Your AI tools (Your sign-in's),
   *  each its own page in Blueprint's Setup (the setup audit); Riverstone keeps them inside their screens. */
  | 'yourplan'
  | 'aitools';

export interface Place {
  view: View;
  label: string;
  /**
   * The permissions that open it, any one of them: the one its screen's own list asks for (packages/auth
   * permissions.ts). Never role names: whichever of a person's roles gives it opens it, a company's own
   * role included (BL-137, QA-680). Absent: everyone in the office app.
   */
  opens?: readonly string[];
  /** The tab it shares with other places; absent: a tab of its own. */
  tab?: string;
}

// Each list is the permissions that open a place or a control, never role names (QA-680, QA-681). The
// system roles holding each are said beside it (packages/auth roles.ts) and
// apps/web/test/permissions-decide.test.ts keeps the two in step, so a person with only system roles
// sees what they always saw.

/** Who sees the books (accounting.view): the owner, the manager and the bookkeeper. */
export const BOOKS_PERMS = ['accounting.view'];
/** The office: who finds customers (customers.search), never a technician or the warehouse. */
export const OFFICE_PERMS = ['customers.search'];
/** Who books work (jobs.book): the owner, the manager, the dispatcher and customer service. */
export const BOOKER_PERMS = ['jobs.book'];
/** Who reviews and completes jobs (jobs.complete): the owner, the manager and the dispatcher. */
export const REVIEW_PERMS = ['jobs.complete'];
/** Who sees Agents: agents.view (the owner and the manager), and whoever decides agent requests (approvals.decide). */
export const AGENTS_PERMS = ['agents.view', 'approvals.decide'];
/** Who sees Memberships (memberships.list: customers.search): the office. */
export const MEMBERSHIP_PERMS = OFFICE_PERMS;
/** Who sees Inventory (inventory.manage): the owner, the manager and the warehouse. */
export const INVENTORY_PERMS = ['inventory.manage'];
/** Who sees Company (getting started: settings.view); the owner also manages the subscription there. */
export const COMPANY_PERMS = ['settings.view'];
/** Who sees the technicians' hours and corrects them (time.correct): the owner, a manager, the bookkeeper. */
export const HOURS_PERMS = ['time.correct'];
/** Who sees ServiceTitan, the move off it (migration.view): the owner, the manager, the dispatcher, the bookkeeper. */
export const MIGRATION_PERMS = ['migration.view'];
/** Who sees the huddle (gp.huddle.view): the owner, the manager, the dispatcher and customer service. */
export const HUDDLE_PERMS = ['gp.huddle.view'];

export const PLACES: readonly Place[] = [
  // Every place names the permissions that open it: a technician (or anyone holding none) sees none (QA-257).
  // Five tabs: Calls · Book (Book a job, Customers) · Dispatch (Dispatch, Hours) · Inbox (To book, Review, Messages, Lists, Tasks) ·
  // Business (Books, Huddle, Reports, Inventory, Agents, Settings, Company, ServiceTitan). The first place under a tab is where it opens.
  { view: 'calls', label: 'Calls', opens: ['calls.view'] },
  { view: 'book', label: 'Book a job', opens: BOOKER_PERMS, tab: 'Book' },
  { view: 'customers', label: 'Customers', opens: OFFICE_PERMS, tab: 'Book' },
  { view: 'memberships', label: 'Memberships', opens: MEMBERSHIP_PERMS, tab: 'Book' },
  // the day board (dispatch.board, jobs.view) for the office: a technician's day is the tech app's
  { view: 'board', label: 'Dispatch', opens: OFFICE_PERMS, tab: 'Dispatch' },
  // the technicians' hours by the week, for those who correct them (time.correct, QA-402): never pay
  { view: 'hours', label: 'Hours', opens: HOURS_PERMS, tab: 'Dispatch' },
  { view: 'tobook', label: 'To book', opens: BOOKER_PERMS, tab: 'Inbox' },
  { view: 'review', label: 'Review', opens: REVIEW_PERMS, tab: 'Inbox' },
  // every text and email to customers, and the ones that couldn't be sent (messages.view: the office)
  { view: 'messages', label: 'Messages', opens: ['messages.view'], tab: 'Inbox' },
  // the office's lists: open jobs, this week, unpaid invoices, follow-ups, and their own (views.*)
  { view: 'lists', label: 'Lists', opens: OFFICE_PERMS, tab: 'Inbox' },
  // tasks and follow-ups: yours, nobody's yet, everyone's (tasks.view, BL-004), the office's
  { view: 'tasks', label: 'Tasks', opens: OFFICE_PERMS, tab: 'Inbox' },
  { view: 'books', label: 'Books', opens: BOOKS_PERMS, tab: 'Business' },
  // the morning huddle: revenue earned by day against the month's goal (gp.huddle.view, BL-007)
  { view: 'huddle', label: 'Huddle', opens: HUDDLE_PERMS, tab: 'Business' },
  // the ServiceTitan reports the office runs, each to whoever holds what it reads (BL-007)
  { view: 'reports', label: 'Reports', opens: OFFICE_PERMS, tab: 'Business' },
  { view: 'inventory', label: 'Inventory', opens: INVENTORY_PERMS, tab: 'Business' },
  { view: 'agents', label: 'Agents', opens: AGENTS_PERMS, tab: 'Business' },
  { view: 'settings', label: 'Settings', opens: ['settings.view'], tab: 'Business' },
  { view: 'company', label: 'Company', opens: COMPANY_PERMS, tab: 'Business' },
  // getting the company's own data out of ServiceTitan (gap #16): the run, mappings, photos, sign-offs
  { view: 'migration', label: 'ServiceTitan', opens: MIGRATION_PERMS, tab: 'Business' },
];

/** Whether a person holding these permissions opens a place. */
export const canSee = (p: Place, held: readonly string[] = []): boolean =>
  !p.opens || holdsAny(held, p.opens);

/**
 * Whether the person is the office's at all (QA-680): they open one of its places, by whichever role
 * gives the permission. A technician and anyone holding none of them are sent to the tech app.
 */
export const inOfficeApp = (held: readonly string[] = []): boolean => PLACES.some((p) => canSee(p, held));

/** A place's own link, #/<view> (QA-335): the address follows the place, so a reload or a link opens it. */
export const PLACE_LINK = /^#\/([a-z]+)$/;

/** The place a link opens, if these permissions open it; Your sign-in is everyone's. */
export function linkedPlace(hash: string, held: readonly string[]): View | null {
  const v = PLACE_LINK.exec(hash)?.[1];
  if (v === 'signin') return v;
  return PLACES.find((p) => p.view === v && canSee(p, held))?.view ?? null;
}

/** Hours' own words for someone it isn't for (Blueprint's Hours, and a link to it, QA-774). */
export const HOURS_NOT_YOURS =
  'Hours are for the owner, managers and the bookkeeper. A manager fixes a technician’s time.';

/**
 * A place's link this person doesn't open, where the place has its own words for them (QA-774): Hours
 * says who it's for, so a dispatcher sent to #/hours reads that on the board, not nothing.
 */
export function notYoursWords(hash: string, held: readonly string[]): string | null {
  const v = PLACE_LINK.exec(hash)?.[1];
  if (v !== 'hours' || linkedPlace(hash, held)) return null;
  return HOURS_NOT_YOURS;
}

export interface Tab {
  label: string;
  places: Place[];
}

/** The tabs a person holding these permissions sees, in order. A tab left with one place takes that place's own name. */
export function tabsFor(held: readonly string[] = []): Tab[] {
  const tabs: Tab[] = [];
  for (const p of PLACES.filter((x) => canSee(x, held))) {
    const key = p.tab ?? p.label;
    const t = tabs.find((x) => (x.places[0]!.tab ?? x.places[0]!.label) === key);
    if (t) t.places.push(p);
    else tabs.push({ label: key, places: [p] });
  }
  for (const t of tabs) if (t.places.length === 1) t.label = t.places[0]!.label;
  return tabs;
}

export interface NavProps {
  tenant: string;
  /** The company's display name: its initials stone (QA-335). */
  companyName?: string;
  view: View;
  /** The part of the screen that's open, where it has parts with links of their own (Books, Settings). */
  part?: string | null;
  /** Open a screen, or one of its parts (#/books/banking). */
  onView: (v: View, part?: string) => void;
  ringing: number;
  /** What waits in the Inbox (QA-344): counted on Inbox, To book and Review, and Inbox opens on work. */
  waiting?: {
    tobook?: number;
    review?: number;
    messages?: number;
    tasks?: number;
    callbacks?: number;
    /** new price book pictures waiting for approval: counted on Setup › Price book (BL-122) */
    pictures?: number;
  };
  onSignOut: () => Promise<void>;
  /** The signed-in person's roles: who they are (the owner), never what opens (that's permissions). */
  roles?: readonly string[];
  /**
   * The permissions the person holds (/auth/session): every role's they hold, a company's own included,
   * and a grant from before roles. What places and controls open by (QA-680, QA-681).
   */
  permissions?: readonly string[];
  /** The signed-in person's own staff record, if they have one: their own row offers them no sign-out (QA-543). */
  staffId?: string | null;
  /** The company's time zone for the header clock (the browser's until it's known). */
  timeZone?: string;
  /** The person's notifications, and what opening one does (the bell shows when both are given). */
  notes?: NotesState;
  onOpenNote?: (n: Note) => void;
}

/** What waits on a place: a ringing call, work to book, jobs to review (QA-344), new replies, tasks due. */
export function waitingOn(nav: Pick<NavProps, 'ringing' | 'waiting'>, v: View, part?: string | null): number {
  if (v === 'calls') return nav.ringing;
  // Setup › Price book: its new pictures waiting for approval (BL-122), on that page of the rail only
  if (v === 'settings') return part === 'pricebook' ? (nav.waiting?.pictures ?? 0) : 0;
  return v === 'tobook' || v === 'review' || v === 'messages' || v === 'tasks' ? (nav.waiting?.[v] ?? 0) : 0;
}

/** The places something can wait on (waitingOn), each said in its own words (waitingWords). */
export const WAITS = ['calls', 'tobook', 'review', 'messages', 'tasks'] as const satisfies readonly View[];

/**
 * What waits on a place, in words: a link's description, never its name, so a link is always called
 * what it is ("Calls", "Inbox") and a ringing call says it rings (QA-359, QA-673).
 */
export function waitingWords(v: (typeof WAITS)[number], n: number): string {
  switch (v) {
    case 'calls':
      return `${n} ringing`;
    case 'tobook':
      return `${n} to book`;
    case 'review':
      return n === 1 ? '1 job awaiting review' : `${n} jobs awaiting review`;
    case 'messages':
      return n === 1 ? '1 new reply' : `${n} new replies`;
    case 'tasks':
      return n === 1 ? '1 of your tasks is due' : `${n} of your tasks are due`;
  }
}

/** "Thu, Oct 15", "6:03" and "p.m." (a small suffix, so the evening never reads as morning), each minute. */
function useClock(timeZone?: string): { day: string; time: string; suffix: string } {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 15_000);
    return () => clearInterval(id);
  }, []);
  const tz = timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  return { day: dateText(now, tz, { weekday: 'short', year: false }), ...timeParts(now, tz) };
}

/** One step of a pattern page's crumb (ui-web's PageHead `crumb`): the place's link back, or the page. */
export interface PageCrumbStep {
  label: string;
  href?: string;
  onPress?: () => void;
}

/**
 * Blueprint's crumb for a page on ui-web's patterns (PageHead's `crumb`), the same steps the Header's
 * Crumb draws: the place, a link to the first of its pages this person opens, then the page's own name;
 * on that first page, the place alone. With `back`, a screen's own page under its place's (an open
 * customer): the place goes back to it, then `title`. Outside the frame (no crumb), none.
 */
export function usePageCrumb(
  nav: NavProps,
  own?: { back: () => void; title: string },
): PageCrumbStep[] | undefined {
  const c = useContext(CrumbContext);
  if (!c) return undefined;
  if (own) return [{ label: c.place, href: `#/${nav.view}`, onPress: own.back }, { label: own.title }];
  const first = c.first as View | null;
  const onFirst = first === nav.view && (!c.firstPart || c.firstPart === nav.part);
  if (!first || onFirst) return [{ label: c.place }];
  return [
    {
      label: c.place,
      href: `#/${first}${c.firstPart ? `/${c.firstPart}` : ''}`,
      onPress: () => nav.onView(first, c.firstPart ?? undefined),
    },
    { label: c.page },
  ];
}

/**
 * What the Header draws over a Blueprint screen's title block, for a page whose title block is ui-web's
 * PageHead (a pattern page): the move off ServiceTitan while it's under way, a trial on the loaded data,
 * the account's and the two-factor banners. The page puts it first, inside its <main>.
 */
export function PageBanners({ nav }: { nav: NavProps }) {
  return (
    <>
      <MigrationBanner tenant={nav.tenant} held={nav.permissions ?? []} here={nav.view === 'migration'} />
      <TrialBanner />
      <AccountBanner
        onPay={() => {
          location.hash = '#billing';
          nav.onView('company');
        }}
      />
      {nav.view !== 'signin' && (
        <TwoFactorBanner owner={!!nav.roles?.includes('owner')} onSetUp={() => nav.onView('signin')} />
      )}
    </>
  );
}

/**
 * Blueprint's crumb over the title (pages/ux-full's .crumb): the place, a link to the first of its pages
 * this person opens, then the page's own name; on that first page, the place alone.
 */
function Crumb({
  nav,
  title,
  back,
  list,
}: {
  nav: NavProps;
  title: string;
  back?: () => void;
  list?: string;
}) {
  const c = useContext(CrumbContext);
  if (!c) return null;
  const first = c.first as View | null;
  const backLink = (words: string) => (
    <a
      href={`#/${nav.view}`}
      onClick={(e) => {
        e.preventDefault();
        back?.();
      }}
    >
      {words}
    </a>
  );
  // on the place's first page (its part too, where it has one) the crumb is the place alone
  const onFirst = first === nav.view && (!c.firstPart || c.firstPart === nav.part);
  return (
    <p className="bp-crumb">
      {back && list ? (
        // an item open on a page that lists them (a call on Calls): the place, the list going back, the item
        <>
          {first && !onFirst ? (
            <a
              href={`#/${first}${c.firstPart ? `/${c.firstPart}` : ''}`}
              onClick={(e) => {
                e.preventDefault();
                nav.onView(first, c.firstPart ?? undefined);
              }}
            >
              {c.place}
            </a>
          ) : (
            c.place
          )}
          {' › '}
          {backLink(list)}
          {' › '}
          {title}
        </>
      ) : back ? (
        // a screen's own page under its place's (an open customer): the place goes back to it
        <>
          <a
            href={`#/${nav.view}`}
            onClick={(e) => {
              e.preventDefault();
              back();
            }}
          >
            {c.place}
          </a>
          {' › '}
          {title}
        </>
      ) : first && !onFirst ? (
        <>
          <a
            href={`#/${first}${c.firstPart ? `/${c.firstPart}` : ''}`}
            onClick={(e) => {
              e.preventDefault();
              nav.onView(first, c.firstPart ?? undefined);
            }}
          >
            {c.place}
          </a>
          {' › '}
          {c.page}
        </>
      ) : (
        c.place
      )}
    </p>
  );
}

export function Header({
  nav,
  title,
  lede,
  children,
  toolbar = false,
  held = false,
  crumb,
}: {
  nav: NavProps;
  title: string;
  /** The tools are the screen's toolbar (Dispatch): beside the title on one line, wrapping beside it. */
  toolbar?: boolean;
  /** Past a required two-factor's deadline (QA-093): no sub-nav, so nothing reads as open. */
  held?: boolean;
  /**
   * Blueprint's crumb on a page within the screen (an open customer): the place goes back to the screen.
   * With `list`, the place stays and the list's own name is the way back (Inbox › Calls › the caller).
   */
  crumb?: { back: () => void; list?: string };
  /** The one line under the title, if the screen needs it. */
  lede?: string;
  children?: ReactNode;
}) {
  // In Blueprint the frame draws the places and the rail (blueprint/Frame.tsx): the header is the
  // screen's banners and its title block.
  const riverstone = useDesign() === 'riverstone';
  const tabs = tabsFor(nav.permissions);
  const here = tabs.find((t) => t.places.some((p) => p.view === nav.view));
  const clock = useClock(nav.timeZone);
  const [help, setHelp] = useState(false);
  // What waits on a place, said as the tab's description, never its name, so a tab is always called
  // what it is: "Calls", "Inbox" (QA-359).
  const waits = (v: View) => waitingOn(nav, v);
  const badge = (n: number, hot: boolean, key?: string) =>
    n > 0 ? (
      <span key={key} className={`count${hot ? ' hot' : ''}`} aria-hidden="true">
        {n}
      </span>
    ) : null;
  const count = (v: View) => badge(waits(v), v === 'calls', v);
  // A tab shows one count, however many of its places have work (Inbox: to book plus review).
  const tabCount = (views: readonly View[]) =>
    badge(
      views.reduce((sum, v) => sum + waits(v), 0),
      views.includes('calls'),
    );
  const described = (views: readonly View[]) => {
    const ids = views.filter((v) => waits(v) > 0).map((v) => `waiting-${v}`);
    return ids.length ? { 'aria-describedby': ids.join(' ') } : {};
  };
  // One landmark holds it all, the title and its line included, so nothing on the page sits outside a
  // landmark (axe "region"); the screen's own content is its <main>. In Riverstone it is the page's
  // banner; in Blueprint the frame's top (blueprint/Frame.tsx) is the banner, so the screen's head is
  // a region named by its title, never a second banner (QA-672).
  const titleId = useId();
  const Head = riverstone ? 'header' : 'section';
  return (
    <Head className="apphead" {...(riverstone ? {} : { 'aria-labelledby': titleId })}>
      {/* the move off ServiceTitan while it's under way: the owner's and managers' (BL-103) */}
      <MigrationBanner tenant={nav.tenant} held={nav.permissions ?? []} here={nav.view === 'migration'} />
      {/* a trial on the loaded data: everyone's told their changes are thrown away */}
      <TrialBanner />
      {riverstone && (
        <div className="topbar">
          <div className="logo" aria-hidden="true">
            {initials(nav.companyName ?? nav.tenant)}
          </div>
          {/* no places (a technician's sign-in): no empty pill beside the logo */}
          {tabs.length ? (
            <nav className="places" aria-label="Office">
              {tabs.map((t) => (
                <button
                  key={t.label}
                  className={`btn quiet${t === here ? ' on' : ''}`}
                  aria-current={t === here ? 'page' : undefined}
                  {...described(t.places.map((p) => p.view))}
                  onClick={() => {
                    // a tab opens on its first place with work waiting (QA-344), else its first place
                    if (t !== here)
                      nav.onView((t.places.find((p) => waits(p.view) > 0) ?? t.places[0]!).view);
                  }}
                >
                  {t.label}
                  {tabCount(t.places.map((p) => p.view))}
                </button>
              ))}
            </nav>
          ) : null}
          <div className="clock">
            <span>{clock.day}</span>{' '}
            <b>
              {clock.time}
              <small>{clock.suffix}</small>
            </b>
          </div>
          {nav.notes && nav.onOpenNote && (
            <NotificationsBell
              notes={nav.notes}
              {...(nav.timeZone ? { timeZone: nav.timeZone } : {})}
              onOpen={nav.onOpenNote}
            />
          )}
          {/* on a phone these shrink to 44 px icons so the first row holds the mark, the clock and all four */}
          <button
            className={`btn soft hbtn${nav.view === 'signin' ? ' on' : ''}`}
            aria-label="Your sign-in"
            aria-current={nav.view === 'signin' ? 'page' : undefined}
            onClick={() => nav.onView('signin')}
          >
            <svg className="ic" viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="8" r="3.5" />
              <path d="M5 20c.8-3.6 3.6-5.5 7-5.5s6.2 1.9 7 5.5" />
            </svg>
            <span className="lbl">Your sign-in</span>
          </button>
          <button
            className="btn soft hbtn"
            aria-label="Help"
            aria-haspopup="dialog"
            onClick={() => setHelp(true)}
          >
            <svg className="ic" viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <path d="M9.6 9.2a2.5 2.5 0 0 1 4.8.9c0 1.7-2.4 2.2-2.4 3.6M12 17h.01" />
            </svg>
            <span className="lbl">Help</span>
          </button>
          <button
            className="btn soft hbtn"
            aria-label="Sign out"
            onClick={() => {
              forgetSessionCaches();
              void nav.onSignOut();
            }}
          >
            <svg className="ic" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M14 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4M10 16l4-4-4-4M14 12H4" />
            </svg>
            <span className="lbl">Sign out</span>
          </button>
        </div>
      )}
      {help && (
        <HelpDialog
          held={nav.permissions ?? []}
          {...(nav.timeZone ? { timeZone: nav.timeZone } : {})}
          onClose={() => setHelp(false)}
        />
      )}
      <AccountBanner
        onPay={() => {
          location.hash = '#billing';
          nav.onView('company');
        }}
      />
      {nav.view !== 'signin' && (
        <TwoFactorBanner owner={!!nav.roles?.includes('owner')} onSetUp={() => nav.onView('signin')} />
      )}
      <div className={`screenhead${toolbar ? ' toolbar' : ''}`}>
        <div>
          {!riverstone && !held && (
            <Crumb
              nav={nav}
              title={title}
              {...(crumb ? { back: crumb.back } : {})}
              {...(crumb?.list ? { list: crumb.list } : {})}
            />
          )}
          <h1 className="screen-title" id={titleId}>
            {title}
          </h1>
          {lede && <p className="screen-lede">{lede}</p>}
        </div>
        {children && <div className="tools">{children}</div>}
      </div>
      {riverstone && !held && here && here.places.length > 1 && (
        <nav className="subnav" aria-label={here.label}>
          {here.places.map((p) => (
            <button
              key={p.view}
              className={`btn quiet${p.view === nav.view ? ' on' : ''}`}
              aria-current={p.view === nav.view ? 'page' : undefined}
              {...described([p.view])}
              onClick={() => nav.onView(p.view)}
            >
              {p.label}
              {count(p.view)}
            </button>
          ))}
        </nav>
      )}
      {riverstone &&
        WAITS.filter((v) => waits(v) > 0).map((v) => (
          <span key={v} id={`waiting-${v}`} hidden>
            {waitingWords(v, waits(v))}
          </span>
        ))}
    </Head>
  );
}
